import { Flex } from '@brightdrop/bd-ui';
import { Box, Divider, Skeleton, Typography } from '@mui/material';
import { Fragment, type ReactNode } from 'react';

import OverviewFieldLabel from './OverviewFieldLabel';
import useStyles from './OverviewFieldList.styles';
import type { OverviewDetailsProps } from './types';

type OverviewFieldListProps = {
  fields: OverviewDetailsProps[];
  testId: string;
  isLoading?: boolean;
  /**
   * 16px vertical padding on each row and no extra divider gap, matching the
   * advisor GMF linked-account field list.
   */
  paddedRows?: boolean;
  /** Content closing out the list, separated by the same hairline as the rows. */
  footer?: ReactNode;
};

/**
 * Stacks fields one per row with the label left and the value right, matching
 * the shape the vehicle list uses for its mobile cards.
 */
const OverviewFieldList = ({
  fields,
  testId,
  isLoading = false,
  paddedRows = false,
  footer,
}: OverviewFieldListProps): JSX.Element => {
  const { classes } = useStyles({ paddedRows });

  return (
    <Box width="100%">
      {fields.map((field, index) => (
        <Fragment key={field.testid}>
          {index > 0 && <Divider className={classes.divider} />}
          <Flex gap={16} className={classes.row}>
            <OverviewFieldLabel
              field={field}
              testId={testId}
              className={classes.label}
              typographyVariant="small_body_regular"
            />
            {isLoading ? (
              <Skeleton width={100} height={30} />
            ) : (
              <Typography
                variant="small_body_medium"
                component="div"
                className={classes.value}
              >
                {field.value}
              </Typography>
            )}
          </Flex>
        </Fragment>
      ))}
      {footer && (
        <>
          <Divider className={classes.divider} />
          <Box data-testid={`${testId}-field-list-footer`}>{footer}</Box>
        </>
      )}
    </Box>
  );
};

export default OverviewFieldList;
